import { Injectable, ComponentRef, ViewContainerRef, Type } from '@angular/core';
import { Capacitor } from '@capacitor/core';
export interface BottomSheetConfig {
  height?: string;
  maxHeight?: string;
  hasBackdrop?: boolean;
  disableClose?: boolean;
  panelClass?: string;
  data?: any;
  disableKeyboardAdjustment?: boolean;
}
export interface BottomSheetRef<T = any> {
  componentRef: ComponentRef<T>;
  close: (result?: any) => void;
  afterClosed: () => Promise<any>;
}
@Injectable({
  providedIn: 'root'
})
export class BottomSheetService {
  private bottomSheetContainer?: ViewContainerRef;
  private activeSheets: BottomSheetRef[] = [];
  setContainer(container: ViewContainerRef) {
    this.bottomSheetContainer = container;
  }
  open<T>(component: Type<T>, config: BottomSheetConfig = {}): BottomSheetRef<T> {
    if (!this.bottomSheetContainer) {
      throw new Error('Bottom sheet container not set. Call setContainer() first.');
    }
    const defaultConfig: BottomSheetConfig = {
      height: 'auto',
      maxHeight: '80dvh',
      hasBackdrop: true,
      disableClose: false,
      panelClass: '',
      ...config
    };
    // Create backdrop
    const backdropElement = this.createBackdrop(defaultConfig);

    // Create bottom sheet container
    const sheetElement = this.createSheetContainer(defaultConfig);

    // Create component
    const componentRef = this.bottomSheetContainer.createComponent(component);

    // If component has data input, set it
    if (defaultConfig.data && componentRef.instance &&
      typeof componentRef.instance === 'object' &&
      componentRef.instance !== null &&
      'data' in componentRef.instance) {
      (componentRef.instance as any).data = defaultConfig.data;
    }
    // Append component to sheet
    sheetElement.appendChild(componentRef.location.nativeElement);

    // Add to DOM
    document.body.appendChild(backdropElement);
    backdropElement.appendChild(sheetElement);
    // Animation
    requestAnimationFrame(() => {
      backdropElement.classList.add('visible');
      sheetElement.classList.add('visible');
    });
    let closeResolve: (value: any) => void;
    const afterClosedPromise = new Promise<any>((resolve) => {
      closeResolve = resolve;
    });
    // Keep sheet above keyboard when it opens on mobile
    // For Android with disableKeyboardAdjustment, skip the listener to prevent viewport adjustment
    const shouldSkipKeyboardAdjustment =
      defaultConfig.disableKeyboardAdjustment && Capacitor.getPlatform() === 'android';
    const cleanupViewport = shouldSkipKeyboardAdjustment
      ? () => {}
      : this.setupViewportResizeListener(sheetElement);

    const bottomSheetRef: BottomSheetRef<T> = {
      componentRef,
      close: (result?: any) => {
        cleanupViewport();
        this.closeSheet(backdropElement, sheetElement, componentRef, result, closeResolve);
      },
      afterClosed: () => afterClosedPromise
    };
    // Set up backdrop click to close
    if (defaultConfig.hasBackdrop && !defaultConfig.disableClose) {
      backdropElement.addEventListener('click', (e) => {
        if (e.target === backdropElement) {
          bottomSheetRef.close();
        }
      });
    }
    // If component has bottomSheetRef property, set it
    if (componentRef.instance &&
      typeof componentRef.instance === 'object' &&
      componentRef.instance !== null &&
      'bottomSheetRef' in componentRef.instance) {
      (componentRef.instance as any).bottomSheetRef = bottomSheetRef;
    }
    this.activeSheets.push(bottomSheetRef);
    return bottomSheetRef;
  }
  private createBackdrop(_: BottomSheetConfig): HTMLElement {
    const backdrop = document.createElement('div');
    backdrop.className = 'bottom-sheet-backdrop';
    backdrop.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 1000;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    return backdrop;
  }
  private createSheetContainer(config: BottomSheetConfig): HTMLElement {
    const sheet = document.createElement('div');
    sheet.className = `bottom-sheet-container ${config.panelClass || ''}`;
    sheet.style.cssText = `
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      background: white;
      border-radius: 16px 16px 0 0;
      max-height: ${config.maxHeight};
      height: ${config.height};
      box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
      transform: translateY(100%);
      transition: transform 0.3s ease, bottom 0.25s ease;
      display: flex;
      flex-direction: column;
      z-index: 1001;
    `;
    return sheet;
  }
  private setupViewportResizeListener(sheet: HTMLElement): () => void {
    // KeyboardResize.None is configured in capacitor.config.ts, so visualViewport does not
    // resize when the keyboard appears. Use MutationObserver to watch --keyboard-height
    // which is set by KeyboardService via @capacitor/keyboard keyboardWillShow/Hide events.
    const observer = new MutationObserver(() => {
      const raw = document.body.style.getPropertyValue('--keyboard-height') || '0px';
      const keyboardHeight = parseInt(raw, 10) || 0;
      sheet.style.bottom = `${keyboardHeight}px`;
      if (keyboardHeight > 0) {
        // Subtract the top safe-area inset so the sheet's top edge (header / close button)
        // stays below the iOS status bar / notch instead of sliding underneath it.
        sheet.style.setProperty(
          'max-height',
          `calc(${window.innerHeight - keyboardHeight}px - env(safe-area-inset-top, 0px))`,
          'important'
        );
      } else {
        sheet.style.removeProperty('max-height');
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });

    return () => observer.disconnect();
  }

  private closeSheet(
    backdrop: HTMLElement,
    sheet: HTMLElement,
    componentRef: ComponentRef<any>,
    result: any,
    resolvePromise: (value: any) => void
  ) {
    // Animate out
    backdrop.classList.remove('visible');
    sheet.classList.remove('visible');

    setTimeout(() => {
      // Clean up
      componentRef.destroy();
      if (backdrop.parentNode) {
        backdrop.parentNode.removeChild(backdrop);
      }

      // Remove from active sheets
      const index = this.activeSheets.findIndex(ref => ref.componentRef === componentRef);
      if (index > -1) {
        this.activeSheets.splice(index, 1);
      }

      resolvePromise(result);
    }, 300);
  }
  closeAll() {
    [...this.activeSheets].forEach(ref => ref.close());
  }
}
